import APIClient from "@/APIClient";
import type { FileParams, FileType } from "@/types/file.types";

export interface UploadResult {
  key: string;
  fileId: number;
  diagnosticImageId?: number;
  /** Vrai si le PUT S3 a réellement réussi (status HTTP 2xx). `fileId` seul ne le
   *  garantit pas : c'est l'id de la ligne DB créée AVANT l'upload, toujours
   *  renseigné même si l'envoi a échoué — ne jamais se fier qu'à sa présence. */
  success: boolean;
}

export async function uploadFilesBatch(
  items: {
    file: File;
    fileType: FileType;
    params: FileParams;
  }[],
  onProgress?: (index: number, percent: number) => void
): Promise<UploadResult[]> {
  const presignedList = await APIClient.uploadFilePresignedBatch(items);

  if (presignedList.length !== items.length) {
    throw new Error(
      `Presigned URL count mismatch: expected ${items.length}, got ${presignedList.length}`
    );
  }

  const limit = 5;
  const finalizeBatchSize = 5;

  // Résultat de l'upload S3 pour chaque item, indexé par position (pas de hole).
  // On NE finalise PAS dans les callbacks onload : voir la note plus bas.
  const uploadOutcomes: {
    fileId: number;
    key: string;
    success: boolean;
    fileType: FileType;
    params: FileParams;
  }[] = new Array(items.length);

  const uploadSingle = async (index: number): Promise<void> => {
    const { uploadUrl, fileId, key } = presignedList[index];
    const { file, fileType, params } = items[index];

    const success = await new Promise<boolean>((resolve) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", uploadUrl, true);
      xhr.setRequestHeader("Content-Type", file.type);

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(index, percent);
        }
      };

      xhr.onload = () => resolve(xhr.status >= 200 && xhr.status < 300);
      xhr.onerror = () => resolve(false);

      xhr.send(file);
    });

    uploadOutcomes[index] = { fileId, key, success, fileType, params };
  };

  // Pool d'uploads S3 concurrents (max `limit` en parallèle).
  const pool: Promise<void>[] = [];
  for (let i = 0; i < items.length; i++) {
    const p: Promise<void> = uploadSingle(i)
      .catch((err) => {
        console.error(`Upload failed for index ${i}`, err);
        // Marque l'item comme échoué pour qu'il soit tout de même finalisé (status FAILED).
        uploadOutcomes[i] = {
          fileId: presignedList[i].fileId,
          key: presignedList[i].key,
          success: false,
          fileType: items[i].fileType,
          params: items[i].params,
        };
      })
      .finally(() => {
        const idx = pool.indexOf(p);
        if (idx !== -1) pool.splice(idx, 1);
      });

    pool.push(p);
    if (pool.length >= limit) await Promise.race(pool);
  }

  await Promise.all(pool);

  // Finalisation APRÈS la fin de tous les uploads, séquentielle et par lots.
  // Auparavant la finalisation se faisait depuis les callbacks onload concurrents
  // en partageant une file mutée à travers un `await`, ce qui pouvait envoyer les
  // mêmes fileId dans plusieurs appels finalize → photos dupliquées/triplées.
  const diagnosticImageIdMap = new Map<number, number>();
  for (let i = 0; i < uploadOutcomes.length; i += finalizeBatchSize) {
    const batch = uploadOutcomes
      .slice(i, i + finalizeBatchSize)
      .map(({ fileId, success, fileType, params }) => ({
        fileId,
        success,
        fileType,
        params,
      }));

    const response = await APIClient.finalizeFileUpload(batch);
    response.forEach((file) => {
      if (file.diagnosticImageId) {
        diagnosticImageIdMap.set(file.id, file.diagnosticImageId);
      }
    });
  }

  return uploadOutcomes.map(({ key, fileId, success }) => ({
    key,
    fileId,
    success,
    diagnosticImageId: diagnosticImageIdMap.get(fileId),
  }));
}
